import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'

import { ApplicationState } from 'src/store'
import {
  Agency,
  Id,
  Request,
  RequestQuote,
  RequestQuoteAir,
  RequestQuoteAirFare,
  RequestTicketSelectedSituation,
  RequestTicketStatus,
  RequestTicketUserData,
  ResumeAirRequestQuoteAirs
} from 'src/types'

type AirWay = Pick<
  RequestQuoteAir,
  | 'air_destiny'
  | 'air_origin'
  | 'company'
  | 'request_quote_air_flights'
  | 'stops'
> &
  Pick<
    RequestQuoteAirFare,
    | 'baggage_amount'
    | 'baggage_weight'
    | 'baggage'
    | 'family'
    | 'request_quote_fare_hash'
    | 'total_value'
  > &
  Pick<RequestQuote, 'expire_date'> & {
    arrival_date_number: number // to sort
    arrival_date: string
    arrival_time: string
    departure_date: string
    departure_time: string
  }

export type SelectedAir = {
  // informações do ticket
  request_ticket_id: Id
  user_data: RequestTicketUserData[]
  agency_data: Agency
  eticket_code: string
  booking_code: string
  total_value?: number
  situation: RequestTicketSelectedSituation
  status: RequestTicketStatus

  // informações do trecho
  request_quote_air_one_way: AirWay
  request_quote_air_round_trip: AirWay
}

function useGetSelectedAirs(request: Request): SelectedAir[] {
  const {
    air: { searches, searchType }
  } = useSelector((state: ApplicationState) => ({
    air: state.air
  }))

  const selectedAirs = useMemo<SelectedAir[]>(() => {
    const items: SelectedAir[] = []
    const selectedWay: AirWay[] = []
    if (!request?.request_id) {
      const list: ResumeAirRequestQuoteAirs[] = []

      const searchKeys = Object.keys(searches)

      searchKeys.forEach(key => {
        searches[key].list.forEach(resumeAirItem => {
          resumeAirItem.request_quote_airs.forEach(quoteAirItem => {
            quoteAirItem.request_quote_air_fares.forEach(fareItem => {
              const hasAdded = !!request.full_request_air.filter(
                fullRequestAirItem =>
                  fullRequestAirItem.request_quote_air_fare_hash.includes(
                    fareItem.request_quote_fare_hash
                  )
              ).length

              if (hasAdded) {
                list.push({
                  ...quoteAirItem,
                  request_quote_air_fares: [{ ...fareItem }]
                })
              }
            })
          })
        })
      })

      list.forEach(airItem => {
        const arrival_date = moment.unix(airItem.arrival_date).utc()
        const departure_date = moment.unix(airItem.departure_date).utc()

        selectedWay.push({
          air_destiny: airItem.air_destiny,
          air_origin: airItem.air_origin,
          arrival_date_number: arrival_date.unix(),
          arrival_date: arrival_date.format('DD/MM/YYYY'),
          arrival_time: arrival_date.format('HH:mm'),
          baggage_amount: airItem.request_quote_air_fares[0].baggage_amount,
          baggage_weight: airItem.request_quote_air_fares[0].baggage_weight,
          baggage: airItem.request_quote_air_fares[0]?.baggage,
          company: airItem.company,
          departure_date: departure_date.format('DD/MM/YYYY'),
          departure_time: departure_date.format('HH:mm'),
          expire_date: null,
          family: airItem.request_quote_air_fares[0].family,
          request_quote_air_flights: airItem.request_quote_air_flights,
          request_quote_fare_hash:
            airItem.request_quote_air_fares[0].request_quote_fare_hash,
          stops: airItem.stops,
          total_value: airItem.request_quote_air_fares[0].total_value
        })
      })

      if (selectedWay?.length) {
        selectedWay.sort(
          (a, b) => a.arrival_date_number - b.arrival_date_number
        )

        if (searchType === 'MULTI-STRETCH') {
          request.request_user_data.forEach(user => {
            selectedWay.forEach(selectedWayItem => {
              items.push({
                agency_data: null,
                booking_code: null,
                eticket_code: null,
                situation: null,
                status: null,
                total_value: selectedWayItem.total_value,
                user_data: [
                  {
                    included: true,
                    owner: true,
                    request_ticket_id: null,
                    request_ticket_user_data_id: null,
                    traveler: user.traveler,
                    user_id: user.user_id
                  }
                ],
                request_quote_air_one_way: { ...selectedWayItem },
                request_quote_air_round_trip: null,
                request_ticket_id: null
              })
            })
          })
        } else {
          request.request_user_data.forEach(user => {
            // for each traveler
            items.push({
              agency_data: null,
              booking_code: null,
              eticket_code: null,
              situation: null,
              status: null,
              total_value: selectedWay.reduce(
                (acc, cur) => acc + cur.total_value,
                0
              ),
              user_data: [
                {
                  included: true,
                  owner: true,
                  request_ticket_id: null,
                  request_ticket_user_data_id: null,
                  traveler: user.traveler,
                  user_id: user.user_id
                }
              ],
              request_quote_air_one_way: { ...selectedWay[0] },
              request_quote_air_round_trip: selectedWay[1]
                ? { ...selectedWay[1] }
                : null,
              request_ticket_id: null
            })
          })
        }
      }
    } else {
      request.request_tickets
        .filter(
          requestTicketItem =>
            requestTicketItem.class_type === 'SERVICE' &&
            requestTicketItem.request_ticket_type === 'AIR'
        )
        .forEach(requestTicketItem => {
          const {
            booking_code,
            request_quote_air_one_way,
            request_quote_air_round_trip
          } =
            requestTicketItem.request_ticket_air_selected.request_quote_air_trip

          const receiptCost = requestTicketItem.request_ticket_receipts[0].cost

          // has unselected fare return
          const familyOneWay =
            request_quote_air_one_way.request_quote_air_fares.find(
              ({ selected }) => selected
            )
          const familyRoundTrip =
            request_quote_air_round_trip.request_quote_air_fares?.find(
              ({ selected }) => selected
            )

          items.push({
            agency_data: {
              ...request_quote_air_one_way.request_quote.customer_supplier
                .supplier.agency
            },
            booking_code,
            eticket_code:
              requestTicketItem.request_ticket_air_selected.eticket_code,
            request_quote_air_one_way: {
              air_destiny: request_quote_air_one_way.air_destiny,
              air_origin: request_quote_air_one_way.air_origin,
              arrival_date_number: request_quote_air_one_way.arrival_date,
              arrival_date: moment
                .unix(request_quote_air_one_way.arrival_date)
                .utc()
                .format('DD/MM/YYYY'),
              arrival_time: moment
                .unix(request_quote_air_one_way.arrival_date)
                .utc()
                .format('HH:mm'),
              baggage_amount: familyOneWay?.baggage_amount,
              baggage_weight: familyOneWay?.baggage_weight,
              expire_date: request_quote_air_one_way.request_quote.expire_date,
              baggage: familyOneWay?.baggage,
              company: request_quote_air_one_way.company,
              departure_date: moment
                .unix(request_quote_air_one_way.departure_date)
                .utc()
                .format('DD/MM/YYYY'),
              departure_time: moment
                .unix(request_quote_air_one_way.departure_date)
                .utc()
                .format('HH:mm'),
              family: familyOneWay?.family,
              request_quote_air_flights:
                request_quote_air_one_way.request_quote_air_flights,
              request_quote_fare_hash: requestTicketItem.request_ticket_id,
              stops: request_quote_air_one_way.stops,
              total_value: familyOneWay.total_value
            },
            request_quote_air_round_trip: !familyRoundTrip
              ? undefined
              : {
                  air_destiny: request_quote_air_round_trip.air_destiny,
                  air_origin: request_quote_air_round_trip.air_origin,
                  arrival_date_number:
                    request_quote_air_round_trip.arrival_date,
                  arrival_date: moment
                    .unix(request_quote_air_round_trip.arrival_date)
                    .utc()
                    .format('DD/MM/YYYY'),
                  arrival_time: moment
                    .unix(request_quote_air_round_trip.arrival_date)
                    .utc()
                    .format('HH:mm'),
                  baggage_amount: familyRoundTrip?.baggage_amount,
                  baggage_weight: familyRoundTrip?.baggage_weight,
                  expire_date:
                    request_quote_air_round_trip.request_quote.expire_date,
                  baggage: familyRoundTrip?.baggage,
                  company: request_quote_air_round_trip.company,
                  departure_date: moment
                    .unix(request_quote_air_round_trip.departure_date)
                    .utc()
                    .format('DD/MM/YYYY'),
                  departure_time: moment
                    .unix(request_quote_air_round_trip.departure_date)
                    .utc()
                    .format('HH:mm'),
                  family: familyRoundTrip?.family,
                  request_quote_air_flights:
                    request_quote_air_round_trip.request_quote_air_flights,
                  request_quote_fare_hash: requestTicketItem.request_ticket_id,
                  stops: request_quote_air_round_trip.stops,
                  total_value: familyRoundTrip?.total_value
                },
            request_ticket_id: requestTicketItem.request_ticket_id,
            situation: requestTicketItem.request_ticket_air_selected.situation,
            status: requestTicketItem.status,
            total_value: receiptCost,
            user_data: requestTicketItem.request_ticket_user_data
          })
        })
    }

    return items
  }, [request, searches, searchType])
  return selectedAirs
}

export default useGetSelectedAirs
